export interface ApiError {
  message?: string;
  code?: string;
  details?: string;
  hint?: string;
  status?: number;
  field?: string;
}

export interface ErrorExplanation {
  title: string;
  description: string;
  suggestion: string;
}

// Error code explanations for common database/API errors
export const ERROR_EXPLANATIONS: Record<string, ErrorExplanation> = {
  "22P02": {
    title: "Invalid Data Format",
    description: "The data provided is not in the expected format.",
    suggestion:
      "Please check that numbers are entered as digits only (no letters or special characters).",
  },
  "23505": {
    title: "Duplicate Entry",
    description: "This record already exists in the system.",
    suggestion: "Try using a different name or identifier that doesn't already exist.",
  },
  "23503": {
    title: "Referenced Data Missing",
    description: "The selected item no longer exists or has been removed.",
    suggestion: "Please refresh the page and try selecting a different option.",
  },
  "23502": {
    title: "Required Field Missing",
    description: "A required field was not provided.",
    suggestion: "Please fill in all required fields marked with an asterisk (*).",
  },
  "42703": {
    title: "System Configuration Error",
    description: "There's an issue with the system configuration.",
    suggestion: "Please contact support if this error persists.",
  },
  VALIDATION_ERROR: {
    title: "Validation Failed",
    description: "The provided data doesn't meet the required criteria.",
    suggestion:
      "Please check the form fields and ensure all data meets the specified requirements.",
  },
  NETWORK_ERROR: {
    title: "Connection Problem",
    description: "Unable to connect to the server.",
    suggestion: "Please check your internet connection and try again.",
  },
  TIMEOUT_ERROR: {
    title: "Request Timed Out",
    description: "The request took too long to complete.",
    suggestion: "Please try again. If the problem persists, try refreshing the page.",
  },
};

// Parse error object to extract meaningful information
// Note: `error` is typed as `any` to preserve the existing public API contract
// of EnhancedErrorDisplay (which accepts arbitrary thrown values).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseError = (error: any): ApiError => {
  if (!error) return { message: "An unknown error occurred" };

  // Handle different error formats
  if (typeof error === "string") {
    return { message: error };
  }

  // Handle API response errors
  if (error.response?.data) {
    const data = error.response.data;
    return {
      message: data.message || data.error || "API request failed",
      code: data.code,
      details: data.details,
      hint: data.hint,
      status: error.response.status,
      field: data.field,
    };
  }

  // Handle fetch errors
  if (error.message) {
    return {
      message: error.message,
      code: error.code || error.name,
      status: error.status,
    };
  }

  // Handle database errors
  if (error.code) {
    return {
      message: error.message || "Database operation failed",
      code: error.code,
      details: error.details,
      hint: error.hint,
    };
  }

  return {
    message: "An unexpected error occurred",
    details: JSON.stringify(error, null, 2),
  };
};

export const copyToClipboard = (text: string): void => {
  navigator.clipboard.writeText(text).catch(() => {
    // Fallback for older browsers
    const textArea = document.createElement("textarea");
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
  });
};
